import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ListCouponsParams, ListPromotionsParams } from "./client";
import { couponsApi, promotionsApi } from "./client";

export const promotionKeys = {
  all: ["loyalty", "promotions"] as const,
  list: (params?: ListPromotionsParams) =>
    [...promotionKeys.all, "list", params] as const,
  detail: (id: string) => [...promotionKeys.all, "detail", id] as const,
  pushMonitor: (id: string) =>
    [...promotionKeys.all, "push-monitor", id] as const,
  summary: () => [...promotionKeys.all, "summary"] as const,
};

export function usePromotionsSummaryQuery() {
  return useQuery({
    queryKey: promotionKeys.summary(),
    queryFn: () => promotionsApi.getSummary(),
  });
}

export function usePromotionsQuery(params?: ListPromotionsParams) {
  return useQuery({
    queryKey: promotionKeys.list(params),
    queryFn: () => promotionsApi.list(params),
  });
}

export function usePromotionQuery(id: string) {
  return useQuery({
    queryKey: promotionKeys.detail(id),
    queryFn: () => promotionsApi.get(id),
    enabled: !!id,
  });
}

export function useCreatePromotionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: promotionsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: promotionKeys.all });
    },
  });
}

export function useResendPromotionPushMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: promotionsApi.resendPush,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: promotionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: promotionKeys.pushMonitor(id) });
      qc.invalidateQueries({ queryKey: promotionKeys.all });
    },
  });
}

export function usePromotionPushMonitorQuery(id: string) {
  return useQuery({
    queryKey: promotionKeys.pushMonitor(id),
    queryFn: () => promotionsApi.pushMonitor(id),
    enabled: !!id,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.hasPendingOutbox) return 3_000;
      return false;
    },
  });
}

export function useUpdatePromotionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof promotionsApi.update>[1];
    }) => promotionsApi.update(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: promotionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: promotionKeys.all });
    },
  });
}

export const couponKeys = {
  all: ["loyalty", "promotions", "coupons"] as const,
  list: (params?: ListCouponsParams) =>
    [...couponKeys.all, "list", params] as const,
  detail: (id: string) => [...couponKeys.all, "detail", id] as const,
  summary: () => [...couponKeys.all, "summary"] as const,
};

export function useCouponsSummaryQuery() {
  return useQuery({
    queryKey: couponKeys.summary(),
    queryFn: () => couponsApi.getSummary(),
  });
}

export function useCouponsQuery(params?: ListCouponsParams) {
  return useQuery({
    queryKey: couponKeys.list(params),
    queryFn: () => couponsApi.list(params),
  });
}

export function useCouponQuery(id: string) {
  return useQuery({
    queryKey: couponKeys.detail(id),
    queryFn: () => couponsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCouponMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: couponsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export function useUpdateCouponMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof couponsApi.update>[1];
    }) => couponsApi.update(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: couponKeys.detail(id) });
      qc.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export function useToggleCouponActiveMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      couponsApi.toggleActive(id, isActive),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: couponKeys.detail(id) });
      qc.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export function useDeleteCouponMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: couponsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}
